'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@repo/ui/components/skeleton';
import { Loader2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useCallback, useState } from 'react';
import { StarIcon } from '@/components/ui/star-icon';
import type { KlaviyoProductSummary, KlaviyoReview } from '@/lib/reviews';
const REVIEWS_PER_PAGE = 5;

// ─── Client-side fetch (via server API route — boost applied server-side) ────

async function fetchMoreReviews(productId: string, offset: number): Promise<{
  reviews: KlaviyoReview[];
}> {
  const url = new URL(`/api/reviews/${productId}`, window.location.origin);
  url.searchParams.set('limit', String(REVIEWS_PER_PAGE));
  url.searchParams.set('offset', String(offset));
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error('Failed to fetch reviews');
  return res.json();
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StarRating(t0) {
  const $ = _c(8);
  const {
    rating,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const filled = Math.round(rating);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      sm: "h-3.5 w-3.5",
      md: "h-5 w-5",
      lg: "h-6 w-6"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const sizeClass = t2[size];
  const t3 = `${rating} out of 5 stars`;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [1, 2, 3, 4, 5];
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== filled || $[3] !== sizeClass) {
    t5 = t4.map(star => <StarIcon key={`star-${star}`} className={`${sizeClass} ${star <= filled ? "fill-brand-accent text-brand-accent" : "fill-neutral-200 text-neutral-200"}`} />);
    $[2] = filled;
    $[3] = sizeClass;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t3 || $[6] !== t5) {
    t6 = <span className="inline-flex items-center gap-0.5" role="img" aria-label={t3}>{t5}</span>;
    $[5] = t3;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function RatingBar(t0) {
  const $ = _c(14);
  const {
    stars,
    count,
    maxCount
  } = t0;
  const percentage = maxCount > 0 ? count / maxCount * 100 : 0;
  const isTop = count === maxCount && count > 0;
  let t1;
  if ($[0] !== stars) {
    t1 = <span className="w-3 flex-shrink-0 text-right text-neutral-600">{stars}</span>;
    $[0] = stars;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <StarIcon className="h-3 w-3 flex-shrink-0 fill-brand-accent text-brand-accent" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `h-full rounded-full ${isTop ? "bg-brand-accent" : "bg-neutral-400"}`;
  const t4 = `${Math.max(percentage, 2)}%`;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      width: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t3 || $[6] !== t5) {
    t6 = <div className="relative h-2 w-48 flex-shrink-0 overflow-hidden rounded-full bg-neutral-200"><div className={t3} style={t5} /></div>;
    $[5] = t3;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== count) {
    t7 = <span className="w-10 flex-shrink-0 text-right text-neutral-600">{count}</span>;
    $[8] = count;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t1 || $[11] !== t6 || $[12] !== t7) {
    t8 = <div className="flex items-center gap-2 text-sm">{t1}{t2}{t6}{t7}</div>;
    $[10] = t1;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function FitSlider(t0) {
  const $ = _c(8);
  const {
    answer,
    label
  } = t0;
  const position = (answer - 1) / 4 * 100;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-sm font-medium text-neutral-700">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${position}%`;
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="relative mt-2 h-1.5 rounded-full bg-neutral-200"><div className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-brand-accent shadow-sm" style={{
        left: t2
      }} /></div>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="mt-1.5 text-center text-xs text-neutral-500">Just right</p>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t3) {
    t5 = <div>{t1}{t3}{t4}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function ReviewCard(t0) {
  const $ = _c(37);
  const {
    review
  } = t0;
  let t1;
  if ($[0] !== review.created_at) {
    const date = new Date(review.created_at);
    t1 = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      timeZone: "UTC"
    });
    $[0] = review.created_at;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedDate = t1;
  let t2;
  if ($[2] !== review.author) {
    t2 = <p className="text-sm font-bold text-neutral-900">{review.author}</p>;
    $[2] = review.author;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== review.verified) {
    t3 = review.verified && <span className="mt-1 flex items-center gap-1.5 text-xs font-medium text-neutral-600"><svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 22 22" aria-hidden="true"><path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.691-.13.635-.08 1.293.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.604-.274 1.262-.144 1.897.13.636.433 1.221.878 1.69.47.446 1.055.749 1.69.879.635.13 1.294.084 1.902-.139.272.587.702 1.087 1.24 1.443s1.167.554 1.813.571c.646-.017 1.272-.215 1.812-.571s.97-.856 1.245-1.443c.604.223 1.262.27 1.897.139.634-.13 1.218-.433 1.687-.879.445-.469.75-1.054.882-1.69.132-.635.083-1.293-.14-1.897.586-.273 1.084-.705 1.439-1.246.355-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" fill="#171717" /></svg>Verified Buyer</span>;
    $[4] = review.verified;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex-shrink-0">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== review.rating) {
    t5 = <StarRating rating={review.rating} size="md" />;
    $[9] = review.rating;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== review.title) {
    t6 = review.title && <span className="text-sm font-bold uppercase text-neutral-900">{review.title}</span>;
    $[11] = review.title;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="flex flex-wrap items-center gap-2">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== review.content) {
    t8 = review.content && <p className="mt-3 text-sm leading-relaxed text-neutral-700">{review.content}</p>;
    $[16] = review.content;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="min-w-0">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== review.syndication_platform) {
    t10 = review.syndication_platform === "shop_app" && <span className="inline-flex items-center gap-1 rounded border border-neutral-300 px-2 py-0.5 text-[11px] font-medium text-neutral-600">Verified by <span className="font-bold text-neutral-900">shop</span></span>;
    $[21] = review.syndication_platform;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== formattedDate) {
    t11 = <span className="text-sm text-neutral-500">{formattedDate}</span>;
    $[23] = formattedDate;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="flex items-start gap-3 text-right">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t4 || $[30] !== t9) {
    t13 = <div className="grid grid-cols-1 gap-4 md:grid-cols-[160px_1fr_auto]">{t4}{t9}{t12}</div>;
    $[28] = t12;
    $[29] = t4;
    $[30] = t9;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span>Was this review helpful?</span>;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <button type="button" className="flex items-center gap-1 transition-colors hover:text-neutral-900" aria-label="Helpful"><ThumbsUp className="h-3.5 w-3.5" /><span>0</span></button>;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="mt-5 flex items-center justify-end gap-3 border-t border-neutral-100 pt-4 text-xs text-neutral-500">{t14}{t15}<button type="button" className="flex items-center gap-1 transition-colors hover:text-neutral-900" aria-label="Not helpful"><ThumbsDown className="h-3.5 w-3.5" /><span>0</span></button></div>;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13) {
    t17 = <article className="border-t border-neutral-200 py-8">{t13}{t16}</article>;
    $[35] = t13;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
}
export function ProductReviewsSkeleton() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="text-center"><Skeleton className="mx-auto h-10 w-48 md:h-12 md:w-56" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton className="h-12 w-12 md:h-14 md:w-14" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center gap-3">{t1}<div><div className="flex gap-0.5">{[1, 2, 3, 4, 5].map(_temp)}</div><Skeleton className="mt-1 h-3 w-32" /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="space-y-2 md:border-l md:border-neutral-200 md:pl-8">{[1, 2, 3, 4, 5].map(_temp2)}</div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mt-12"><div className="flex flex-col items-center gap-8 md:flex-row md:items-center md:justify-center md:gap-12">{t2}{t3}<div className="md:border-l md:border-neutral-200 md:pl-8"><Skeleton className="h-4 w-24" /><Skeleton className="mt-3 h-1.5 w-36 rounded-full" /><Skeleton className="mx-auto mt-2 h-3 w-20" /></div></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mt-10 space-y-8">{["review-a", "review-b"].map(_temp3)}</div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <section className="bg-white py-16" id="reviews">{t0}{t4}{t5}<div className="mt-8 flex justify-center"><Skeleton className="h-10 w-40" /></div></section>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}

// ─── Main component ──────────────────────────────────────────────────────────
function _temp3(id) {
  return <article key={id} className="border-t border-neutral-200 py-8"><div className="grid grid-cols-1 gap-4 md:grid-cols-[160px_1fr_auto]"><div><Skeleton className="h-4 w-20" /><Skeleton className="mt-2 h-3 w-24" /></div><div><div className="flex items-center gap-2"><div className="flex gap-0.5">{[1, 2, 3, 4, 5].map(n_1 => <Skeleton key={`${id}-star-${n_1}`} className="h-4 w-4 rounded-sm" />)}</div><Skeleton className="h-4 w-32" /></div><Skeleton className="mt-3 h-4 w-full" /><Skeleton className="mt-2 h-4 w-5/6" /></div><div className="flex justify-end"><Skeleton className="h-4 w-16" /></div></div></article>;
}
function _temp2(n_0) {
  return <div key={`review-bar-${n_0}`} className="flex items-center gap-2 text-sm"><Skeleton className="h-3 w-3" /><Skeleton className="h-2 w-48 rounded-full" /><Skeleton className="h-3 w-8" /></div>;
}
function _temp(n) {
  return <Skeleton key={`review-summary-star-${n}`} className="h-5 w-5 rounded-sm" />;
}
interface ProductReviewsProps {
  reviews: KlaviyoReview[];
  product: KlaviyoProductSummary;
  /** Numeric Shopify product ID for client-side "load more" fetching */
  productId: string;
  /** Market money locale, so server and client format numbers the same way */
  moneyLocale: string;
}
export function ProductReviews({
  reviews: initialReviews,
  product,
  productId,
  moneyLocale
}: ProductReviewsProps) {
  const {
    star_rating,
    review_count,
    rating_histogram
  } = product;
  const maxCount = Math.max(...rating_histogram);
  const fitAnswer = Object.values(product.question_answers).find(qa => qa.label === 'Fit' || qa.label === 'Value for Money');
  const [allReviews, setAllReviews] = useState<KlaviyoReview[]>(initialReviews);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const hasMore = allReviews.length < review_count;
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const data = await fetchMoreReviews(productId, allReviews.length);
      if (data.reviews?.length) {
        setAllReviews(prev => [...prev, ...data.reviews]);
      }
    } catch {
      // Silently fail — user can retry
    } finally {
      setIsLoadingMore(false);
    }
  }, [productId, allReviews.length, isLoadingMore, hasMore]);
  return <section className="bg-white py-16" id="reviews">
			{/* ── Header ── */}
			<div className="text-center">
				<h2 className="text-4xl font-black uppercase tracking-tight text-neutral-900 md:text-5xl">
					Reviews
				</h2>
			</div>

			{/* ── Summary bar ── */}
			<div className="mt-12">
				{/* Mobile: centered rating + stars only */}
				<div className="flex flex-col items-center gap-1 md:hidden">
					<div className="flex items-center gap-3">
						<span className="text-5xl font-bold leading-none text-brand-accent">
							{star_rating.toFixed(1)}
						</span>
						<StarRating rating={star_rating} size="lg" />
					</div>
					<p className="text-xs text-neutral-500">
						Based on {review_count.toLocaleString(moneyLocale)} reviews
					</p>
				</div>

				{/* Desktop: centered full rating summary row */}
				<div className="hidden md:flex md:items-center md:justify-center md:gap-12">
					<div className="flex items-center gap-3">
						<span className="text-5xl font-bold leading-none text-brand-accent">
							{star_rating.toFixed(1)}
						</span>
						<div>
							<StarRating rating={star_rating} size="lg" />
							<p className="mt-1 text-xs text-neutral-500">
								Based on {review_count.toLocaleString(moneyLocale)} reviews
							</p>
						</div>
					</div>

					<div className="space-y-1 border-l border-neutral-200 pl-8">
						{[5, 4, 3, 2, 1].map(stars => <RatingBar key={`bar-${stars}`} stars={stars} count={rating_histogram[stars - 1] || 0} maxCount={maxCount} />)}
					</div>

					{fitAnswer && <div className="border-l border-neutral-200 pl-8">
							<div className="w-36">
								<FitSlider answer={fitAnswer.answer} label={fitAnswer.label} />
							</div>
						</div>}
				</div>
			</div>

			{/* ── Divider ── */}
			<div className="mt-10 border-t border-neutral-200" />

			{/* ── Individual reviews ── */}
			<div>
				{allReviews.map(review => <ReviewCard key={`review-${review.id}`} review={review} />)}
			</div>

			{/* ── Load more ── */}
			{hasMore && <div className="mt-8 text-center">
					<button type="button" className="rounded border border-neutral-300 px-10 py-3 text-xs font-bold uppercase tracking-[0.15em] text-neutral-900 transition-colors hover:bg-neutral-100 disabled:opacity-50" onClick={handleLoadMore} disabled={isLoadingMore}>
						{isLoadingMore ? <span className="inline-flex items-center gap-2">
								<Loader2 className="h-4 w-4 animate-spin" />
								Loading...
							</span> : 'Show More Reviews'}
					</button>
				</div>}
		</section>;
}